import { imu2AdSlotSizesQuickNews } from "@config/AdsConstants";
import { CompanionAdsImu2 } from "@elements/Advertisement/variant/CompanionAdImu";
import {
  loadLottieAnimation,
  type LottieConfig,
  type LottieInstance,
} from "@helper/lottieHelper";
import { PageAdTargetingTypeEnum } from "@typings/Ads.d";
import { AdsCardProps } from "@typings/QuickNews";
import { ReactElement, useEffect, useMemo, useRef } from "react";

type QNAdCardProps = {
  item: AdsCardProps;
  onNext: () => void;
  isMobile: boolean;
};

const MOVE_THRESHOLD = 6;

export default function QnAdCard({
  item,
  onNext,
  isMobile,
}: QNAdCardProps): ReactElement {
  const canvasId = `qn-ad-read-more-${item.id}`;
  const lottieInstanceRef = useRef<LottieInstance | null>(null);
  const pageAdTargetValue = PageAdTargetingTypeEnum.LISTING;
  const startPos = useRef({ x: 0, y: 0 });
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const activeAd = useRef(false);

  // Memoize IMU slot mapping
  const imu2AdSlots = useMemo(
    () =>
      isMobile
        ? imu2AdSlotSizesQuickNews.mobile
        : imu2AdSlotSizesQuickNews.desktop,
    [isMobile],
  );

  useEffect(() => {
    const isActive = { current: true };

    const config: LottieConfig = {
      animationPath: "quick-news/readMore.lottie",
      canvasId,
      dimensions: {
        height: 29,
        minHeight: 29,
      },
    };

    const instance = loadLottieAnimation(config);
    if (isActive.current && instance) {
      lottieInstanceRef.current = instance;
    }

    return () => {
      isActive.current = false;
      if (lottieInstanceRef.current) {
        lottieInstanceRef.current.destroy();
        lottieInstanceRef.current = null;
      }
    };
  }, [canvasId]);

  const handleEventDown = (e: React.PointerEvent) => {
    if (!e.isPrimary) return;
    startPos.current = { x: e.clientX, y: e.clientY };
  };

  const handleEventUp = (e: React.PointerEvent) => {
    if (!e.isPrimary) return;
    const dx = Math.abs(e.clientX - startPos.current.x);
    const dy = Math.abs(e.clientY - startPos.current.y);

    const noGesture = dx < MOVE_THRESHOLD && dy < MOVE_THRESHOLD;
    if (noGesture) toggleEventNone();
  };

  const toggleEventNone = () => {
    const innerAd =
      wrapperRef.current?.querySelector<HTMLElement>(".imu-subroot");
    if (!innerAd) {
      console.warn(
        "QnAdCard: .imu-subroot not found - ad may not be loaded yet",
      );
      return;
    }

    const isActive = !activeAd.current;
    innerAd.classList.toggle("pointer-events-none", !isActive);
    activeAd.current = isActive;
  };

  return (
    <div
      data-testid="qn-adcard-container"
      key={item.id}
      className="w-full h-full bg-black/30"
      style={{ touchAction: "pan-y" }}
    >
      <div
        ref={wrapperRef}
        onPointerDown={handleEventDown}
        onPointerUp={handleEventUp}
        className="items-center inline"
      >
        <CompanionAdsImu2
          uniqueSectionName="quick-news"
          pageAdTargetType={pageAdTargetValue}
          pageNumber={item.adIndex || 1}
          rootClassName="h-full my-auto lg:h-full"
          subRootClassName="relative flex h-full my-auto items-center justify-center lg:relative lg:top-0 pointer-events-none"
          overrideImu2AdSlots={imu2AdSlots}
        />
      </div>

      <button
        data-testid="qn-adcard-next-btn"
        aria-label="Next card"
        onClick={onNext}
        className="absolute bottom-4 w-full text-white flex flex-col items-center"
      >
        {/* Scroll for more news */}
        Swipe for more news
        <canvas
          data-testid="qn-adcard-animation"
          id={canvasId}
          className="mx-auto w-[11px]"
        />
      </button>
    </div>
  );
}
